import { getJSON, postJSON } from 'common/services/api'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { LogResponse } from '@lmc/ts-common'

const INCIDENT_CATEGORY_ITEM = '5e867702b349f776c8d48c34'

type UseLogProps = {
  logId: string | undefined
  onUpdateSuccess?: () => void
  onRemoveSuccess?: () => void
  onRestoreSuccess?: () => void
}

const useLog = ({
  logId,
  onUpdateSuccess,
  onRestoreSuccess,
  onRemoveSuccess,
}: UseLogProps) => {
  const queryClient = useQueryClient()

  const { data, isLoading: isLogLoading } = useQuery<{
    result: LogResponse
  }>(['logs', logId], () => getJSON({ url: `list/logs/${logId}` }), {
    enabled: logId !== undefined,
  })

  const updateFunction = (values: any) =>
    postJSON({
      url: `list/logs/${logId}`,
      method: 'PUT',
      json: values,
    })

  const { mutateAsync: updateMutation } = useMutation(
    ['logs', logId],
    updateFunction,
    {
      onSuccess: () => {
        onUpdateSuccess?.()
        queryClient.invalidateQueries(['logs', logId])
        queryClient.invalidateQueries(['incident-report', logId])
        if (data?.result?.category === INCIDENT_CATEGORY_ITEM) {
          queryClient.invalidateQueries(['list-incidents'])
        }
      },
    }
  )

  const removeFunction = () =>
    postJSON({
      url: `list/logs/${logId}`,
      method: 'PUT',
      json: { deleted: true },
    })

  const { mutateAsync: removeMutation, isLoading: isRemoveLoading } =
    useMutation(['logs', logId], removeFunction, {
      onSuccess: () => {
        onRemoveSuccess?.()
        queryClient.invalidateQueries(['logs', logId])
        queryClient.invalidateQueries(['incident-report', logId])
        if (data?.result?.category === INCIDENT_CATEGORY_ITEM) {
          queryClient.invalidateQueries(['list-incidents'])
        }
      },
    })

  const restoreFunction = () =>
    postJSON({
      url: `list/logs/${logId}`,
      method: 'PUT',
      json: { deleted: false },
    })

  const { mutateAsync: restoreMutation, isLoading: isRestoreLoading } =
    useMutation(['logs', logId], restoreFunction, {
      onSuccess: () => {
        onRestoreSuccess?.()
        queryClient.invalidateQueries(['logs', logId])
      },
    })

  return {
    updateLog: updateMutation,
    removeLog: removeMutation,
    isRemoveLoading,
    restoreLog: restoreMutation,
    isRestoreLoading,
    isLogLoading,
    log: data?.result,
  }
}

export default useLog
